import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import SimpleBar from 'simplebar-react';
import { useSnackbar } from 'notistack';
import { get } from '@truefit/http-utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChevronDown, ListFilter } from 'lucide-react';

import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { useAuth } from '@/features/scaffolding/components/AuthContext';
import { useCurrentGame } from '../context/CurrentGameContext';
import useGameClipGroupsQuery from '@/features/clipGroups/hooks/useGameClipGroupsQuery';
import useClipDownloadMutation from '@/features/clips/hooks/useClipDownloadMutation';
import useEnqueueClipForExportMutation from '@/features/clips/hooks/useEnqueueClipForExportMutation';
import useCancelClipExportMutation from '@/features/clips/hooks/useCancelClipExportMutation';
import Tracking from '@/shared/services/tracking';
import CameraDownloadIcon from '@/features/ses/components/CameraDownloadIcon';
import { getDownloadState } from '@/features/ses/components/getDownloadState';
import ExpandChevronButton from '@/shared/components/ExpandChevronButton';
import { PANEL_PADDING } from '../constants';
import { useTranscodingProfiles } from '@/shared/context/TranscodingProfileContext';
import { ClipGroupModel } from '@/features/clipGroups/types';
import { ClipModel, ClipStatus } from '@/features/clips/types';

type ClipWithCamera = ClipModel & {
  gameCamera?: { id: number; displayName: string | null };
};

type ClipsTab = 'all' | 'my' | 'automated';

interface ClipRow {
  clipGroup: ClipGroupModel;
  clip: ClipWithCamera;
}

type ClipSort = 'gameTime' | 'created';

const SORT_OPTIONS: { value: ClipSort; label: string; hint: string }[] = [
  { value: 'created', label: 'Newest first', hint: 'Created' },
  { value: 'gameTime', label: 'Game time', hint: 'P1 → P3' },
];

// clipGroup.inTime is the instant the clip starts in the game feed, so ascending
// inTime is chronological game order for every sport. period/gameTime can't be
// used: they are display strings ("P1", "12:34") and hockey's clock counts down,
// so a larger gameTime is the *earlier* moment. Groups predating inTime fall back
// to createdAt, which for a live-clipped game is the same ordering.
const gameTimeKey = (row: ClipRow): number =>
  new Date(row.clipGroup.inTime ?? row.clipGroup.createdAt).getTime();

// A row is one clip, and each camera of a group is its own clip, so "newest
// created" keys on the clip rather than on its group.
const createdKey = (row: ClipRow): number => new Date(row.clip.createdAt).getTime();

// clip.id is monotonic, so it breaks ties (the cameras of one clip group share a
// game moment, and can share a creation timestamp) into a stable order.
const clipIdKey = (row: ClipRow): number => Number(row.clip.id);

const sortClipRows = (rows: ClipRow[], sort: ClipSort): ClipRow[] =>
  [...rows].sort((a, b) =>
    sort === 'created'
      ? createdKey(b) - createdKey(a) || clipIdKey(b) - clipIdKey(a)
      : gameTimeKey(a) - gameTimeKey(b) || clipIdKey(a) - clipIdKey(b),
  );

// Duration to show checkmark before transitioning to download icon
const READY_TRANSITION_DELAY_MS = 5000;

// Helper functions for clip row formatting
const formatGametime = (clipGroup: ClipGroupModel): string => {
  if (clipGroup.period && clipGroup.gameTime) {
    return `${clipGroup.period} ${clipGroup.gameTime}`;
  }
  if (clipGroup.inTime) {
    const date = new Date(clipGroup.inTime);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  return '—';
};

const resolveTrackedObject = (trackedObjectId: string | undefined, findPlayerById: any, objectOfPlay: string): string => {
  if (trackedObjectId) {
    const player = findPlayerById(trackedObjectId);
    if (player) return player.displayName || `Player ${trackedObjectId}`;
  }
  return objectOfPlay || 'Fixed Camera';
};

interface ClipRowItemProps {
  clipGroup: ClipGroupModel;
  clip: ClipWithCamera;
  onDownloadButtonClick: (clip: ClipWithCamera) => void;
  onCopyLink: (clipId: string | number) => void;
  isLinkAvailable: boolean;
  findPlayerById: any;
  objectOfPlay: string;
}

const ClipRowItem: React.FC<ClipRowItemProps> = ({
  clipGroup,
  clip,
  onDownloadButtonClick,
  onCopyLink,
  isLinkAvailable,
  findPlayerById,
  objectOfPlay,
}) => {
  const baseDownloadState = getDownloadState(clip.status, true);
  const prevStatusRef = useRef<ClipStatus | string | undefined>(clip.status);
  const { profiles, getMode } = useTranscodingProfiles();

  // Output format of the exported file. The profile lives on the group, not the
  // clip, but that still describes each clip exactly: changing a group's mode
  // soft-deletes its clips (PATCH .../clip-groups/{clipGroupId}) and this panel
  // only lists non-deleted ones, so every row here was exported under the
  // group's current profile. Null while the id map loads, so the badge appears
  // once rather than flashing a wrong value; a group with no profile is
  // landscape, which is what the exporter falls back to.
  const outputMode = profiles
    ? clipGroup.outputProfileId != null
      ? getMode(clipGroup.outputProfileId)
      : 'landscape'
    : null;
  const [showReadyCheckmark, setShowReadyCheckmark] = useState(false);

  // Detect transition TO ready state and show checkmark
  useEffect(() => {
    const normalizedCurrent = typeof clip.status === 'string' ? clip.status.toUpperCase() : clip.status;
    const normalizedPrev = typeof prevStatusRef.current === 'string'
      ? prevStatusRef.current.toUpperCase()
      : prevStatusRef.current;

    // If status just changed to READY (wasn't READY before)
    if (normalizedCurrent === ClipStatus.READY && normalizedPrev !== ClipStatus.READY) {
      setShowReadyCheckmark(true);

      // After 5 seconds, transition to download state
      const timer = setTimeout(() => {
        setShowReadyCheckmark(false);
      }, READY_TRANSITION_DELAY_MS);

      prevStatusRef.current = clip.status;
      return () => clearTimeout(timer);
    }

    prevStatusRef.current = clip.status;
  }, [clip.status]);

  // Determine final display state
  // - If transitioning (showReadyCheckmark), show 'ready' (checkmark)
  // - If already ready but not transitioning, show 'download' (arrow)
  // - Otherwise use base state
  let downloadState = baseDownloadState;
  if (baseDownloadState === 'ready') {
    downloadState = showReadyCheckmark ? 'ready' : 'download';
  }

  const clipStatusNormalized = clip.status?.toUpperCase();
  const isLinkDisabled = clipStatusNormalized === ClipStatus.ERROR;

  return (
    <Stack
      key={clip.id}
      direction="row"
      sx={(theme) => ({
        alignItems: 'center',
        py: 0.5,
        minHeight: 32,
        backgroundColor: theme.palette.cx.bg.surface2,
        mb: 0.5,
        p: '4px 8px 4px 8px',
        borderRadius: 1,
      })}
    >
      <Typography
        variant="caption"
        sx={{ minWidth: 50, flexShrink: 0, color: 'text.secondary', mr: 1 }}
      >
        {formatGametime(clipGroup)}
      </Typography>
      <Typography
        variant="caption"
        noWrap
        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', mr: 1 }}
      >
        {clipGroup.name}
      </Typography>
      <Typography
        variant="caption"
        noWrap
        sx={(theme) => ({
          minWidth: 80,
          flexShrink: 0,
          fontFamily: 'Sohne',
          fontWeight: 400,
          fontStyle: 'Buch',
          fontSize: '14px',
          leadingTrim: 'NONE',
          lineHeight: '20px',
          letterSpacing: 0,
          color: theme.palette.cx.text.secondary,
          mr: 2,
        })}
      >
        {resolveTrackedObject(clipGroup.trackedObjectId, findPlayerById, objectOfPlay)}
      </Typography>
            {outputMode && (
        <Tooltip title={outputMode === 'portrait' ? 'Portrait' : 'Landscape'}>
          <Box sx={{ display: 'flex', flexShrink: 0, mr: 1 }}>
            {outputMode === 'portrait' ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Portrait clip"
              >
                <rect
                  x="5.9"
                  y="2.4"
                  width="8.2"
                  height="15.2"
                  rx="1.6"
                  stroke="#9797A5"
                  strokeWidth="0.8"
                />
                <path d="M8.5 3.75L11.5 3.75" stroke="#9797A5" strokeWidth="0.5" strokeLinecap="round" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Landscape clip"
              >
                <rect
                  x="17.6"
                  y="5.4"
                  width="8.2"
                  height="15.2"
                  rx="1.6"
                  transform="rotate(90 17.6 5.4)"
                  stroke="#9797A5"
                  strokeWidth="0.8"
                />
                <path d="M8 15.5L12 15.5" stroke="#9797A5" strokeWidth="0.6" strokeLinecap="round" />
              </svg>
            )}
          </Box>
        </Tooltip>
      )}
      <Typography
        variant="caption"
        sx={{
          minWidth: 30,
          flexShrink: 0,
          fontFamily: 'Sohne',
          fontWeight: 500,
          fontStyle: 'Kräftig',
          fontSize: '14px',
          leadingTrim: 'NONE',
          lineHeight: '20px',
          letterSpacing: 0,
          mr: 1,
        }}
      >
        {clip.autoDirector
          ? 'AD'
          : clip.gameCamera?.displayName
          ? clip.gameCamera.displayName
              .split(/\s+/)
              .map((w) => w[0])
              .join('')
              .toUpperCase()
          : `C${clip.cameraId}`}
      </Typography>

      <IconButton
        size="small"
        disabled={!isLinkAvailable}
        onClick={() => onCopyLink(clip.id)}
        sx={{ p: 0.5 }}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="14"
            cy="14"
            r="13.5"
            fill="#17171C"
            stroke={downloadState === 'ready' ? '#ffffff' : '#DBDBE1'}
          />
          <path
            d="M14.5434 11.792C14.9766 11.9989 15.3538 12.3066 15.6434 12.6895C15.933 13.0724 16.1265 13.5192 16.2076 13.9923C16.2888 14.4655 16.2552 14.9512 16.1096 15.4087C15.9641 15.8661 15.7109 16.282 15.3714 16.6214L12.3714 19.6213C11.8087 20.184 11.0457 20.5 10.25 20.5C9.45436 20.5 8.6913 20.184 8.12869 19.6213C7.56607 19.0587 7.25 18.2957 7.25 17.5C7.25 16.7044 7.56607 15.9413 8.12869 15.3787L9.30002 14.2074M18.2 13.7927L19.3713 12.6214C19.934 12.0587 20.25 11.2957 20.25 10.5C20.25 9.70436 19.934 8.9413 19.3713 8.37869C18.8087 7.81607 18.0457 7.5 17.25 7.5C16.4544 7.5 15.6913 7.81607 15.1287 8.37869L12.1287 11.3787C11.7891 11.718 11.5359 12.1339 11.3904 12.5914C11.2449 13.0488 11.2113 13.5346 11.2924 14.0077C11.3735 14.4809 11.567 14.9277 11.8566 15.3105C12.1462 15.6934 12.5235 16.0012 12.9567 16.208"
            stroke={downloadState === 'ready' ? '#ffffff' : '#DBDBE1'}
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </IconButton>
      <CameraDownloadIcon
        state={downloadState}
        onClick={() => onDownloadButtonClick(clip)}
        size="small"
      />
    </Stack>
  );
};

interface ClipSortMenuProps {
  value: ClipSort;
  onChange: (value: ClipSort) => void;
}

const ClipSortMenu: React.FC<ClipSortMenuProps> = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const active = SORT_OPTIONS.find((option) => option.value === value) ?? SORT_OPTIONS[0];

  const handleSelect = (next: ClipSort) => {
    setAnchorEl(null);
    onChange(next);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<ListFilter />}
        endIcon={<ChevronDown />}
        sx={{ flexShrink: 0, color: 'text.primary' }}
      >
        {active.label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ListSubheader
          disableSticky
          sx={(theme) => ({
            backgroundColor: 'transparent',
            color: theme.palette.cx.text.tertiary,
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.08em',
            lineHeight: '16px',
            textTransform: 'uppercase',
          })}
        >
          Sort by
        </ListSubheader>
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => handleSelect(option.value)}
            sx={(theme) => ({
              gap: 3,
              justifyContent: 'space-between',
              '&.Mui-selected': {
                backgroundColor: theme.palette.cx.bg.elevated,
                '&:hover': { backgroundColor: theme.palette.cx.bg.elevated2 },
              },
            })}
          >
            {option.label}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {option.hint}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface ClipDownloadsPanelProps {
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
}

const ClipDownloadsPanel: React.FC<ClipDownloadsPanelProps> = ({
  expanded,
  onExpand,
  onCollapse,
}) => {
  const gameId = useCurrentGameId();
  const { currentUser } = useAuth();
  const { findPlayerById, objectOfPlay } = useCurrentGame();
  const { clipGroups } = useGameClipGroupsQuery(gameId);
  const downloadMutation = useClipDownloadMutation();
  const enqueueClipForExportMutation = useEnqueueClipForExportMutation();
  const cancelClipExportMutation = useCancelClipExportMutation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { zeroTouchClipping } = useFlags();

  const [activeTab, setActiveTab] = useState<ClipsTab>('all');
  const [clipSort, setClipSort] = useState<ClipSort>('created');

  // Not a board-specced event — named to match the existing Switch * family.
  const handleClipsTabChange = useCallback(
    (tab: ClipsTab) => {
      if (tab === activeTab) return;
      Tracking.getInstance().track('Switch Clips Tab', { category: 'Clips', tab });
      setActiveTab(tab);
    },
    [activeTab],
  );

  const handleClipSortChange = useCallback((sort: ClipSort) => {
    Tracking.getInstance().track('Sort Clips', { category: 'Clips', sort });
    setClipSort(sort);
  }, []);

  // Shadow launch (C360-2813): zeroTouchClipping gates only the visibility of the
  // Automated Clips tab. Detection, clip creation and export keep running for every
  // org so the pipeline can be observed in production, and users flagged in still get
  // the tab to verify the clips it produced. Nothing below narrows the data we fetch.
  const automatedTabVisible = Boolean(zeroTouchClipping);

  // Derive the rendered tab so a flag that turns off — or that has not resolved yet —
  // falls back to All Clips instead of leaving the panel on a tab that is no longer
  // selectable.
  const currentTab: ClipsTab =
    activeTab === 'automated' && !automatedTabVisible ? 'all' : activeTab;

  // Zero-touch clips (clipGroup.source === 'automated') live in their own tab;
  // the manual tabs exclude them so user-created clips stay uncluttered.
  const allClipRows = useMemo<ClipRow[]>(() => {
    if (!clipGroups.length) return [];
    return clipGroups
      .filter((group) => group.source !== 'automated')
      .flatMap((group) => (group.clips || []).map((clip) => ({ clipGroup: group, clip })));
  }, [clipGroups]);

  const myClipRows = useMemo<ClipRow[]>(() => {
    if (!currentUser?.id || !clipGroups.length) return [];
    return clipGroups
      .filter((group) => group.source !== 'automated' && group.clouduserId === currentUser.id)
      .flatMap((group) => (group.clips || []).map((clip) => ({ clipGroup: group, clip })));
  }, [clipGroups, currentUser?.id]);

  const automatedClipRows = useMemo<ClipRow[]>(() => {
    if (!clipGroups.length) return [];
    return clipGroups
      .filter((group) => group.source === 'automated')
      .flatMap((group) => (group.clips || []).map((clip) => ({ clipGroup: group, clip })));
  }, [clipGroups]);

  const clipRows = useMemo(
    () =>
      sortClipRows(
        currentTab === 'all' ? allClipRows : currentTab === 'my' ? myClipRows : automatedClipRows,
        clipSort,
      ),
    [currentTab, allClipRows, myClipRows, automatedClipRows, clipSort],
  );


  const handleCopyLink = async (clipId: string | number) => {
    try {
      const response = await get<{ downloadUrl: string }>(
        `/v1.0/clips/${clipId}/download?video=true&purpose=playback`,
      );
      if (response.data.downloadUrl) {
        await navigator.clipboard.writeText(response.data.downloadUrl);
        Tracking.getInstance().track('Export Clip URL', { category: 'Clips', clipId });
        enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
      }
    } catch (err) {
      console.error('Failed to copy clip URL:', err);
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
    }
  };

  const handleDownload = (clipId: string | number) => {
    downloadMutation.mutate({ clipId: String(clipId), video: true, source: 'clip_downloads_panel' });
  };

  const handleDownloadButtonClick = async (clip: ClipWithCamera) => {
    const clipStatusNormalized = clip.status?.toUpperCase();

    // Handle ERROR status: retry the export
    if (clipStatusNormalized === ClipStatus.ERROR) {
      try {
        await enqueueClipForExportMutation.mutateAsync(clip.id as number);
        enqueueSnackbar('Retrying clip export...', { variant: 'info' });
      } catch (error) {
        enqueueSnackbar(
          `Failed to retry export: ${error instanceof Error ? error.message : 'Unknown error'}`,
          { variant: 'error' }
        );
      }
      return;
    }

    // Handle PROCESSING status: cancel the export
    if (clipStatusNormalized === ClipStatus.PROCESSING) {
      try {
        await cancelClipExportMutation.mutateAsync({ clipId: clip.id as number });
        enqueueSnackbar('Canceling clip export...', { variant: 'info' });
      } catch (error) {
        enqueueSnackbar(
          `Failed to cancel export: ${error instanceof Error ? error.message : 'Unknown error'}`,
          { variant: 'error' }
        );
      }
      return;
    }

    // Default behavior: download the clip (READY status)
    handleDownload(clip.id);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#1F1F25',
        borderRadius: 1,
      }}
    >
      {/* Header — visible even at min panel height */}
      <Stack
        direction="row"
        sx={{ px: PANEL_PADDING, flexShrink: 0, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Stack direction="row" sx={{ gap: 2, py: 2, alignItems: 'center' }}>
          <Typography
            variant="body2"
            onClick={() => handleClipsTabChange('all')}
            sx={{
              fontWeight: 500,
              color: currentTab === 'all' ? '#FFF' :  theme.palette.cx.text.muted,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: '16px',
              lineHeight: '22px',
            }}
          >
            All Clips
          </Typography>
          <Typography
            variant="body2"
            onClick={() => handleClipsTabChange('my')}
            sx={{
              fontWeight: 500,
              color: currentTab === 'my' ? '#FFF' : theme.palette.cx.text.muted,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: '16px',
              lineHeight: '22px',
            }}
          >
            My Clips
          </Typography>
          {automatedTabVisible && (
            <Typography
              variant="body2"
              onClick={() => handleClipsTabChange('automated')}
              sx={{
                fontWeight: 500,
                color: currentTab === 'automated' ? '#FFF' : theme.palette.cx.text.muted,
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '16px',
                lineHeight: '22px',
              }}
            >
              Automated Clips
            </Typography>
          )}
        </Stack>

        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#9797A5' }}>
            {/* Denominator is always the All Clips count: automated clips are
                counted separately so they don't inflate the total. */}
            {`${clipRows.length}/${allClipRows.length} clips`}
          </Typography>
          {expanded && <ClipSortMenu value={clipSort} onChange={handleClipSortChange} />}
          <ExpandChevronButton expanded={expanded} onExpand={onExpand} onCollapse={onCollapse} />
        </Stack>
      </Stack>
      {expanded && clipRows.length === 0 && (
        <Box
          sx={{
            width: '100%',
            py: 5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              px: PANEL_PADDING,
              color: '#FFF',
              fontFamily: 'Sohne',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '20px',
            }}
          >
            No clips have been generated yet.
          </Typography>
        </Box>
      )}
      {/* Scrollable clip rows */}
      {expanded && clipRows.length > 0 && (
        <SimpleBar
          style={{
            flex: 1,
            minHeight: 0,
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          {clipRows.map(({ clipGroup, clip }) => {
            const clipStatusNormalized = clip.status?.toUpperCase();
            const isLinkAvailable = clipStatusNormalized !== ClipStatus.ERROR; // Disable link only when ERROR

            return (
              <ClipRowItem
                key={clip.id}
                clipGroup={clipGroup}
                clip={clip}
                onDownloadButtonClick={handleDownloadButtonClick}
                onCopyLink={handleCopyLink}
                isLinkAvailable={isLinkAvailable}
                findPlayerById={findPlayerById}
                objectOfPlay={objectOfPlay}
              />
            );
          })}
        </SimpleBar>
      )}
    </Box>
  );
};

export default React.memo(ClipDownloadsPanel);
